/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React from "react"
import { useTheme } from "@cd2/theme"
import { colors } from "../providers/ThemeProvider"
import { iconMap } from "../const/iconMap"

import { ToolTip } from "@cd2/components"

type ButtonVariant = "primary" | "secondary" | "white" | "danger" | "success" | "text"
type ButtonSize = "small" | "medium" | "large" | "none"

const sizeStyles: Record<ButtonSize, { padding: string; fontSize: string, margin: string }> = {
  none: {
    padding: "4px 8px",
    fontSize: "14px",
    margin: "-4px -8px",
  },
  small: {
    padding: "8px 14px",
    fontSize: "14px",
    margin: "0",
  },
  medium: {
    padding: "11px 20px",
    fontSize: "15px",
    margin: "0",
  },
  large: {
    padding: "14px 28px",
    fontSize: "17px",
    margin: "0",
  },
}

interface PixelButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  icon?: string
  children?: React.ReactNode
  fullWidth?: boolean
  size?: ButtonSize
  tooltip?: React.ReactNode
}

const variantStyles: Record<ButtonVariant, { bg: string; color: string; border: string }> = {
  primary: {
    bg: colors.primary,
    color: "#ffffff",
    border: "#183059",
  },
  secondary: {
    bg: colors.secondary,
    color: "#000000",
    border: "#d4a91a",
  },
  white: {
    bg: "#ffffff",
    color: colors.default,
    border: "#cccccc",
  },
  danger: {
    bg: "#ff4444",
    color: "#ffffff",
    border: "#cc0000",
  },
  success: {
    bg: "#44ff44",
    color: "#000000",
    border: "#00cc00",
  },
  text: {
    bg: "transparent",
    color: colors.default,
    border: "transparent",
  },
}

export function PixelButton({
  variant = "primary",
  icon,
  children,
  fullWidth = false,
  size = "medium",
  disabled,
  tooltip,
  ...props
}: PixelButtonProps) {
  const { borderRadius } = useTheme()
  const style = variantStyles[variant]
  const sizeStyle = sizeStyles[size]
  const isTextVariant = variant === "text"
  
  // Get the full Font Awesome class string from the icon map
  const iconClass = icon ? iconMap[icon] : null

  const buttonElement = (
    <button
      {...props}
      disabled={disabled}
      css={css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: ${sizeStyle.padding};
        
        font-size: ${sizeStyle.fontSize};
        font-weight: 600;
        font-family: inherit;
        border: ${isTextVariant ? "none" : `1px solid ${style.border}`};
        background-color: ${style.bg};
        color: ${style.color};
        border-radius: ${borderRadius}px;
        cursor: ${disabled ? "not-allowed" : "pointer"};
        transition: all 0.1s ease;
        width: ${fullWidth ? "100%" : "auto"};
        opacity: ${disabled ? 0.6 : 1};
        position: relative;
        outline: none;

        /* Pixel art flat style - no shadows, solid borders */
        box-shadow: none;

        &:hover:not(:disabled) {
          background-color: ${isTextVariant ? "rgba(0, 0, 0, 0.05)" : variant === "primary" ? "#1457a1" : variant === "secondary" ? "#f5d01a" : variant === "white" ? "#f5f5f5" : variant === "danger" ? "#ff2222" : "#22ff22"};
        }

        &:active:not(:disabled) {
          background-color: ${isTextVariant ? "rgba(0, 0, 0, 0.1)" : variant === "primary" ? "#1a3fb8" : variant === "secondary" ? "#d4a91a" : variant === "white" ? "#e5e5e5" : variant === "danger" ? "#cc0000" : "#00cc00"};
        }

        &:focus {
          outline: ${isTextVariant ? "none" : `2px solid ${style.border}`};
          outline-offset: ${isTextVariant ? "0" : "2px"};
        }
      `}
    >
      {iconClass && (
        <i
          className={iconClass}
          css={css`
            font-size: inherit;
            line-height: 1;
          `}
        />
      )}
      {children}
    </button>
  )

  if (tooltip) {
    return (
      <div css={css`margin: ${sizeStyle.margin};`}>
        <ToolTip hoverContent={() => tooltip}>
          {buttonElement}
        </ToolTip>
      </div>
    )
  }

  return (
    <div css={css`margin: ${sizeStyle.margin};`}>
      {buttonElement}
    </div>
  )
}

